<template lang="pug">
  CommonLoadBox(:loaded="loaded")
    template(slot='skeleton')
      VideoRedPacketSkeleton
    template(slot='module')
      .video-red-packet-container(v-if="taskList && taskList.length > 0")
        .video-red-packet-header
          img.video-red-packet-title(:src="titleIcon" alt="")
          .video-red-packet-btn(
            :class="{ 'video-red-packet-btn-disabled': !hasActive }"
            @click="handleMainBtnClick"
          ) {{ btnName }}
        .video-red-packet-content(ref="videoRedPacketList")
          template(v-for="(item, index) in taskList")
            .video-red-packet-item(
              ref="videoRedPacketItem"
              :class="{ 'active': item.curActive, 'disabled': item.finished }"
              @click="handleItemClick(item)"
            )
              .video-red-packet-item-num {{ item.amountText }}
</template>

<script>
import eventBus from '@/const/eventBus';
import { getSignAndRecommend } from '@/api/fuli';
import { getTokenAndKey, scrollToMid } from '@/utils';
import { ParamsUtil } from '@/utils/params';
import { safeJsonStringify } from '@/utils/tools';
import Toast from '@/components/toast';
import Loading from '@/components/loading';
import CommonLoadBox from '@/components/common-load-box';
import SPM from '../common/spm';
import { loadPlatformAds, showPlatformAds } from '../common/bridge';
import {
  processAdParams,
  videoRedPacketDataAdapter,
  handleFinishTask,
  loadRewardResultPlatformAds,
} from '../common/helper';
import VideoRedPacketSkeleton from '../skeleton-cpns/video-red-packet-skeleton';
import { WELFARE_VERSION, ADV_SCENE } from '../consts';
import {
  trackRewardPopupExpose,
  trackNextVideo,
  trackAdvClick,
  trackPopupClose,
} from '../common/video-spm';

export default {
  name: 'VideoRedPacket',
  components: {
    CommonLoadBox,
    VideoRedPacketSkeleton,
  },
  props: {},
  data() {
    return {
      // 标题icon
      titleIcon: '',
      // 任务列表
      taskList: [],
      // 主按钮名称
      btnName: '',
      // 是否有可看的视频
      hasActive: false,
      // 视频红包基础信息
      baseInfo: {},
      // 翻倍广告信息
      doubleAdvInformation: null,
      // 当前激活的任务索引
      activeIdx: -1,
      // 视频任务完成错误
      videoFinishedError: false,
      // 是否加载完成
      loaded: false,
    };
  },
  inject: ['taskScene'],
  mounted() {
    // 首次数据接收监听
    eventBus.$on('videoRedPacketDataSent', (moduleInfo) => {
      this.handleVideoRedPackeData(moduleInfo, true);
    });
    SPM.moduleExpose({
      moduleId: 'platform_adver_videoButton',
      contentId: 'platform_adver_videoButton',
      businessInfo: {},
    });
  },
  methods: {
    // 更新视频红包数据
    async updateVideoRedPackeData() {
      try {
        const taskScene = this.taskScene();
        const { token } = await getTokenAndKey() || {};
        // 获取最新数据
        const { data = {} } = await getSignAndRecommend({
          token,
          newWelfare: taskScene === WELFARE_VERSION.NEW_TASK_SCENE_V2,
        }) || {};
        const isSuccess = this.handleVideoRedPackeData(data.moduleInfo);
        return { success: isSuccess };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('updateVideoRedPackeData => error', safeJsonStringify(error));
        return { success: false };
      }
    },
    // 视频红包数据处理
    handleVideoRedPackeData(data, isFirst = false) {
      const {
        titleIcon,
        hasActive,
        btnName,
        taskList = [],
        baseInfo,
        curActiveIdx,
        doubleAdvInformation = null,
      } = videoRedPacketDataAdapter(data) || {};

      this.taskList = taskList;
      this.titleIcon = titleIcon;
      this.btnName = btnName;
      this.hasActive = hasActive;
      this.baseInfo = baseInfo;
      this.doubleAdvInformation = doubleAdvInformation;
      this.loaded = true;

      // 将高亮的视频红包滚动到中间位置
      this.$nextTick(() => {
        const { videoRedPacketItem, videoRedPacketList } = this.$refs || {};
        if (!videoRedPacketItem || !videoRedPacketList) return;
        if (curActiveIdx !== -1) {
          scrollToMid(this.$refs.videoRedPacketItem[curActiveIdx], this.$refs.videoRedPacketList);
        }
      });

      if (taskList && taskList.length > 0 && isFirst) {
        // 模块曝光埋点
        SPM.moduleExpose({
          moduleId: 'Welfare_adver',
          contentId: 'Welfare_adver',
          businessInfo: {},
        });
      }

      // 判断视频完成后任务是否推进成功
      if (!isFirst && curActiveIdx !== -1) {
        return curActiveIdx > this.activeIdx;
      }
      return true;
    },
    // 处理视频红包点击事件
    handleItemClick(item) {
      SPM.clickButton({
        buttonName: 'platform_adver_videoButton',
        businessInfo: {
          buttuntype: '1',
        },
      });
      const { finished, curActive } = item || {};
      if (finished) return;
      // 加载视频广告弹窗
      this.loadAndShowVideo({
        clickType: curActive ? '2' : '3',
      });
    },
    // 处理主按钮点击事件
    handleMainBtnClick() {
      SPM.clickButton({
        buttonName: 'platform_adver_videoButton',
        businessInfo: {
          buttuntype: '0',
        },
      });
      if (!this.hasActive) return;
      // 加载视频广告弹窗
      this.loadAndShowVideo({ clickType: '1' });
    },
    // 加载&展示视频广告
    async loadAndShowVideo(options) {
      // 点击类型 1-看视频 2-观看 3-红包
      const { clickType } = options || {};
      const { unitId, taskCode } = this.baseInfo || {};
      let loadErrorInfo = null;
      let isCatchError = '';
      // 查找当前激活的任务
      const activeIdx = this.taskList.findIndex((item) => item.curActive);
      const activeItem = this.taskList[activeIdx];
      const { amount } = activeItem || {};
      this.activeIdx = activeIdx;

      try {
        Loading.show();
        // 1.加载广告视频
        const loadRes = await loadPlatformAds({
          pageName: 'rewardVideo',
          timeout: '5',
          scene: 'rebateBonus',
          adsource: ParamsUtil.getParams().adsource || '',
          adsInfo: {
            adUnitId: unitId,
            rewardCount: amount,
            taskCode,
            business_line: 'mars',
          },
        });

        if (loadRes && loadRes.pageId) {
          Loading.close();
          // 2.播放广告视频
          const showRes = await showPlatformAds({
            pageId: loadRes.pageId,
          });
          const { errCode, inspireType } = showRes || {};
          if (errCode) {
            Toast.show('任务未完成');
            return;
          }

          // 3.推进任务完成
          const { success } = await handleFinishTask({
            inspireType,
            bannerTaskRelationCouponInfo: this.baseInfo,
          });
          if (!success) {
            return;
          }

          // 4.更新视频红包列表数据
          const { success: updateSuccess } = await this.updateVideoRedPackeData();

          // 5.加载奖励发放弹窗
          if (updateSuccess) {
            this.handleLoadRewardPopup();
          } else {
            Toast.show('任务未完成');
          }
        } else {
          Loading.close();
          Toast.show('拉取视频失败，请稍后再试');
          isCatchError = '0';
          loadErrorInfo = loadRes || {};
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('loadAndShowVideo => error', safeJsonStringify(error));
        Toast.show('拉取视频失败，请稍后再试');
        Loading.close();
        isCatchError = '1';
        loadErrorInfo = error || {};
      }

      // 点击埋点上报
      SPM.clickButton({
        buttonName: 'Welfare_adver',
        businessInfo: {
          clickarea: clickType,
          ladderIndex: String(this.activeIdx + 1),
        },
      });

      // 视频加载错误埋点上报
      if (isCatchError || isCatchError) {
        SPM.customEventWithPointId({
          pointId: 'apmError_videoLoadError',
          businessInfo: {
            isCatchError,
            errorInfo: safeJsonStringify(loadErrorInfo),
          },
        });
      }
    },
    // 加载奖励发放弹窗
    async handleLoadRewardPopup() {
      let isEnd = true;
      let curItem = {};
      let nextItem = {};

      // 判断当前激活的任务索引存在，则获取当前任务和下一个任务
      if (this.activeIdx !== -1) {
        isEnd = this.activeIdx === this.taskList.length - 1;
        curItem = this.taskList[this.activeIdx] || {};
        nextItem = this.taskList[this.activeIdx + 1] || {};
      }

      // 获取当前埋点公共参数
      const getTrackInfo = () => ({
        impids: this.baseInfo?.unitId || '',
        taskid: this.baseInfo?.taskCode || '',
        task_id: this.baseInfo?.taskCode || '',
        ladderIndex: String(this.activeIdx + 1),
        advScene: ADV_SCENE.VIDEO_RED_PACKET,
        advSceneDetail: String(this.activeIdx + 1),
      });

      Loading.show();
      // 获取弹窗广告信息
      const adsInfo = processAdParams(this.doubleAdvInformation || {});
      // 弹窗参数
      const params = {
        pageName: 'rewardResult',
        timeout: '2',
        scene: 'rebateBonus',
        adsource: ParamsUtil.getParams().adsource || '',
        adsInfo,
        otherInfo: {
          data: {
            isEnd,
            rewardCount: curItem.amount || '',
            nextRewardCount: nextItem.amount || '',
          },
        },
      };
      // eslint-disable-next-line no-console
      console.log('handleLoadRewardPopup => params', safeJsonStringify(params));
      // 加载弹窗
      const loadRes = await loadPlatformAds(params);
      const { bannerTaskRelationCouponInfo } = this.doubleAdvInformation || {};
      this.doubleAdvInformation = null;
      if (loadRes && loadRes.pageId) {
        Loading.close();
        // 播放金币收起动画
        eventBus.$emit('playCollectAnimate');

        // 激励视频发奖弹窗曝光埋点
        trackRewardPopupExpose({
          ...getTrackInfo(),
          adv: adsInfo.adUnitId && adsInfo.rewardCount ? '0' : '1',
        });

        // 展示弹窗
        const showRes = await showPlatformAds({
          pageId: loadRes.pageId,
        });
        const {
          inspireType,
          action,
          adsInfo: { rewardTotalCount = '' } = {},
        } = showRes || {};
        switch (action) {
          // 下一个视频
          case 'nextVideo':
            this.loadAndShowVideo();
            trackNextVideo(getTrackInfo());
            break;
            // 翻倍视频广告
          case 'videoBannerReward':
            this.handleBannerReward({
              inspireType,
              bannerTaskRelationCouponInfo,
              isEnd,
              rewardTotalCount,
              nextItem,
            });
            trackAdvClick(getTrackInfo());
            break;
            // 翻倍banner广告
          case 'bannerReward':
            this.handleBannerReward({
              inspireType,
              bannerTaskRelationCouponInfo,
              isEnd,
              rewardTotalCount,
              nextItem,
            });
            trackAdvClick(getTrackInfo());
            break;
          // 关闭弹窗
          case 'close':
            trackPopupClose(getTrackInfo());
            break;
          default:
            break;
        }
      }
      Loading.close();
    },
    // 处理翻倍广告奖励
    async handleBannerReward({
      inspireType, bannerTaskRelationCouponInfo, isEnd, rewardTotalCount, nextItem,
    }) {
      const result = await loadRewardResultPlatformAds({
        inspireType,
        bannerTaskRelationCouponInfo,
        otherInfo: {
          data: {
            isEnd,
            rewardCount: rewardTotalCount,
            nextRewardCount: nextItem.amount || '',
            isTotalReward: true,
          },
        },
      });
      const { action } = result || {};
      if (action === 'nextVideo') {
        this.loadAndShowVideo();
      }
    },
  },
};
</script>

<style lang="less" scoped>
.video-red-packet-container {
  margin-bottom: 0.08rem;
  background-color: #fff;
  border-radius: 0.12rem;
  padding-bottom: 0.14rem;

  .video-red-packet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.1rem 0.16rem 0.05rem 0.12rem;
  }

  .video-red-packet-title {
    width: auto;
    height: 0.22rem;
  }

  .video-red-packet-btn {
    display: flex;
    align-items: center;
    padding: 0 0.16rem;
    height: 0.26rem;
    border-radius: 0.26rem;
    background-color: #ff5526;
    line-height: normal;
    font-weight: 500;
    font-size: 0.12rem;
    color: #fff;

    &.video-red-packet-btn-disabled {
      background-color: #bcc4cc;
    }
  }

  .video-red-packet-content {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-end;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: 0 0.12rem 0;
    height: 0.6rem;

    &::-webkit-scrollbar {
      display: none;
    }

    .video-red-packet-item {
      display: flex;
      justify-content: center;
      align-items: flex-end;
      flex-shrink: 0;
      margin-right: 0.1rem;
      width: 0.48rem;
      height: 0.55rem;
      background-image: url('https://m.hellobike.com/resource/gallery/833/lQdSSD0S4h.png');
      background-size: 100% 100%;
      background-repeat: no-repeat;

      &:last-child {
        margin-right: 0;
      }

      .video-red-packet-item-num {
        margin-bottom: 0.06rem;
        font-family: 'DIN Alternate Bold', sans-serif;
        font-size: 0.14rem;
        line-height: 0.16rem;
        color: #ffeab2;
      }

      &.active {
        position: relative;
        overflow: hidden;
        width: 0.5rem;
        height: 0.6rem;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 60%;
          height: 100%;
          background:
            linear-gradient(
              to right,
              rgba(255, 255, 255, 0) 0%,
              rgba(255, 243, 202, 0.5) 50%,
              rgba(255, 255, 255, 0) 100%
            );
          transform: skewX(-45deg);
          animation: scan 1.5s ease-in-out infinite;
        }

        @keyframes scan {
          0% {
            left: -120%;
          }

          100% {
            left: 180%;
          }
        }

        .video-red-packet-item-num {
          font-size: 0.16rem;
          line-height: 0.19rem;
        }
      }

      &.disabled {
        width: 0.48rem;
        height: 0.55rem;
        background-image: url('https://m.hellobike.com/resource/gallery/833/y_CbCsyZnZ.png');

        .video-red-packet-item-num {
          color: #f9f9f9;
        }
      }
    }
  }
}
</style>
